import cn from "classnames";
import { shallowEqual } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState, ChangeEvent } from "react";

// MUI
import {
    Box,
    Checkbox,
    Dialog,
    DialogContent,
    Divider,
    FormGroup,
    FormControlLabel,
    IconButton,
    Typography,
    CircularProgress,
} from "@mui/material";

// Components
import NavButton from "components/HeaderNavigation/NavButton";
import {
    extractIdFromPath,
    FilterFieldButton,
    getInitialFetchResponse,
    getListsFetchResponse,
    getPersonMetaUI,
    getProjectSearchAiBrief,
    getSearchAiLeadersLongList,
    getSearchAiLeadersShortList,
    TInitialFetch,
    TInitialFetchError,
} from "pages/ProjectDashboard";

// Store
import { convertedOpenSearchData } from "store/slice/Team";
import { setShortList } from "store/slice/refinement/shortList";
import { DraxRole, getRoles, getRolesThunk } from "store/slice/Team/roles";
import { fetchPersonSet } from "store/slice/Project/peopleSet/peopleSetSlice";
import { fetchProjectBrief, getBriefById } from "store/slice/Project/briefs/briefsSlice";
import { getShortList, setLongListWithIn, useLongListSlice } from "store/slice/refinement";
import { fetchProjectSearchAi, getSearchAiById } from "store/slice/Project/searchAis/searchAisSlice";

// Hooks
import { useIntl } from "react-intl";
import { useAppSelector, useAppDispatch } from "app/hooks";
import { TVacancy } from "components/Autocomplete/AutocompleteField/hooks/useAutocompleteVacancy";
import AutocompleteField, { useAutocompleteVacancy } from "components/Autocomplete/AutocompleteField";

// Utils
import { capitalize, isEmpty, isNil } from "lodash";

// Styles
import CRMExportStyles from "./CRMExport.styles";

// Services
import { CRMVacancies, requestPeopleData, talentPotentialMembersPayloadColumns } from "services";
import { addContactsToCrm, breakArrayIntoChunks, fetchContactsByVacancy, saveContactToVacancy } from "services";

// Icons
import icons from "enums/icons";
import CustomIcon from "components/CustomIcon";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

// Types
import { Member } from "store/slice/Team/team.types";
import { BriefChildren, ProjectPersonSet, ProjectSearchAICreationResponse } from "services/projects/project.types";
import { urlPaths } from "enums/urlPaths";

type MemberExported = Member & {
    isExported?: boolean;
};

type TCRMExportProps = {
    open: boolean;
    searchAiId: string | number | null;
    onClose: (state: boolean) => void;
};

const CRMExport = ({ open, searchAiId, onClose }: TCRMExportProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const classes = CRMExportStyles();
    const { pathname } = useLocation();

    const [searchParams] = useSearchParams();
    const { projectId: currentProjectId } = useParams();

    const projectId = searchParams.get("projectId") || currentProjectId;

    const isSetTeamPage = pathname.includes(urlPaths.Team);

    const shortList = useAppSelector(getShortList);
    const { longList } = useLongListSlice();

    const { data: searchAiData } = useAppSelector(getSearchAiById(Number(searchAiId)), shallowEqual);

    const [shouldFetchVacancies, setShouldFetchVacancies] = useState(false);

    const { data: roles } = useAppSelector(getRoles);
    const { vacancyList, getOptionLabelVacancy, renderOptionVacancy } = useAutocompleteVacancy(shouldFetchVacancies);

    const briefPath = getProjectSearchAiBrief(searchAiData)?.ref?.path ?? "";
    const briefId = extractIdFromPath(briefPath);
    const { data: releatedBrief } = useAppSelector(getBriefById(Number(briefId)), shallowEqual);

    const loadingMessages = {
        generic: intl.formatMessage({ id: "crm.dialog.loading.message.generic" }),
        exporting: intl.formatMessage({ id: "crm.dialog.loading.message.exporting" }),
    };

    const [loadingState, setLoadingState] = useState({ status: false, message: loadingMessages.generic });
    const [filterValue, setFilterValue] = useState<string>("");
    const [members, setMembers] = useState<MemberExported[]>([]);
    const [checkboxesState, setCheckboxesState] = useState({ shortList: true, longList: true });
    const [selectedVacancy, setSelectedVacancy] = useState<CRMVacancies | null | undefined>(
        releatedBrief?.data?.crm_vacancy
    );

    const areAllVisibleMembersSelected = members
        .filter((member) => !member.isExported && member.visible)
        .every((member) => member.isSelected);

    // Initial Fetch
    useEffect(() => {
        const initialFetch = async () => {
            setLoadingState({ message: loadingMessages.generic, status: true });

            const promises = [];
            let result = {} as TInitialFetch | TInitialFetchError;

            if (isEmpty(searchAiData) && projectId && searchAiId) {
                promises.push(
                    dispatch(
                        fetchProjectSearchAi({
                            projectId: Number(projectId),
                            path: `/project_${projectId}/search_ai_list/search_ai_${searchAiId}`,
                        })
                    )
                );
            }

            if (!roles.length) {
                promises.push(dispatch(getRolesThunk()));
            }

            if (promises.length && projectId) {
                const response = await Promise.allSettled(promises);
                result = getInitialFetchResponse(response);

                if ("error" in result) {
                    console.error(result.reason);
                    setLoadingState({ message: loadingMessages.generic, status: false });
                    return;
                }
            }

            const searchAiParam = "searchAi" in result ? result.searchAi : searchAiData;
            const rolesParam = "roles" in result ? result.roles : roles;
            const vacancy = await getVacancyFromBrief(searchAiParam);

            if (isSetTeamPage) {
                await getSelectedVacancyContacts([...shortList, ...longList], vacancy as CRMVacancies);
                return;
            }

            const lists = await getUserLists(searchAiParam, rolesParam);

            if (!lists) {
                setLoadingState({ message: loadingMessages.generic, status: false });
                return;
            }

            await getSelectedVacancyContacts(lists, vacancy as CRMVacancies);
        };

        const getVacancyFromBrief = async (searchAiData: ProjectSearchAICreationResponse | undefined) => {
            if (searchAiData === undefined || roles === undefined || projectId === null) return;

            const briefPath = getProjectSearchAiBrief(searchAiData)?.ref?.path;

            if (!briefPath) {
                setShouldFetchVacancies(true);
                return;
            }

            const releatedBrief: BriefChildren = unwrapResult(
                await dispatch(
                    fetchProjectBrief({
                        projectId: Number(projectId),
                        path: briefPath,
                    })
                )
            );
            const briefVacancy = releatedBrief?.data?.crm_vacancy;

            if (!isEmpty(briefVacancy)) {
                setSelectedVacancy(briefVacancy);
                return briefVacancy;
            } else {
                setShouldFetchVacancies(true);
            }
        };

        const getUserLists = async (
            searchAiData: ProjectSearchAICreationResponse | undefined,
            roles: DraxRole[] | undefined
        ) => {
            if (isNil(projectId) || searchAiData === undefined || roles === undefined) return false;

            const promises = [];
            const shortlistPath = getSearchAiLeadersShortList(searchAiData)?.ref?.path;
            const longlistPath = getSearchAiLeadersLongList(searchAiData)?.ref?.path;

            if (shortlistPath) {
                promises.push(dispatch(fetchPersonSet({ projectId, path: shortlistPath })));
            }

            if (longlistPath) {
                promises.push(dispatch(fetchPersonSet({ projectId, path: longlistPath })));
            }

            const response = await Promise.allSettled(promises);
            const result = getListsFetchResponse<ProjectPersonSet>(response);

            if ("error" in result) {
                setLoadingState({ message: loadingMessages.generic, status: false });
                console.error(result.reason);
                return false;
            }

            const [shortListSetList, longListSetList] = result;

            const shortListSet = shortListSetList?.data.person_ids.map((id) => {
                const metaUI = getPersonMetaUI(shortListSetList, id);
                return { ...metaUI, id };
            });

            const longListSet = longListSetList?.data.person_ids.map((id, i) => {
                const metaUI = getPersonMetaUI(longListSetList, id);
                return { ...metaUI, id };
            });

            if (shortListSet?.length || longListSet?.length) {
                const response = await fetchListsData(shortListSet, longListSet, roles);
                const noResults = response.some((val) => val === false);

                return noResults ? false : response;
            }
        };

        const fetchListsData = async (shortList: any[], longList: any[], roles: DraxRole[]) => {
            const result = await requestPeopleData(
                [...(shortList?.map((s) => s.id) || []), ...(longList?.map((s) => s.id) || [])],
                talentPotentialMembersPayloadColumns
            );

            const peopleWithData = convertedOpenSearchData(result);

            const shortListData = peopleWithData
                .filter((p: any) => shortList.map((e) => e.id).includes(Number(p.person_id)))
                .map((member: any, i: number) => {
                    const memberMetaUI = shortList.find((e: any) => e.id === Number(member.person_id));
                    return {
                        ...member,
                        person_id: Number(member.person_id),
                        in: "shortList",
                        statusUI: "enabled" as const,
                        ...(memberMetaUI?.role_id
                            ? {
                                  roleKey: `${memberMetaUI.role_id}`,
                                  role: roles.find((r) => r.id === Number(memberMetaUI.role_id))?.name,
                              }
                            : {}),
                    };
                });

            const longListData = peopleWithData
                .filter((p: any) => longList.map((e) => e.id).includes(Number(p.person_id)))
                .map((member: any, i: number) => {
                    const memberMetaUI = longList.find((e: any) => e.id === Number(member.person_id));
                    return {
                        ...member,
                        person_id: Number(member.person_id),
                        in: memberMetaUI?.in,
                        statusUI: "enabled" as const,
                        ...(memberMetaUI?.role_id
                            ? {
                                  roleKey: `${memberMetaUI.role_id}`,
                                  role: roles.find((r) => r.id === Number(memberMetaUI.role_id))?.name,
                              }
                            : {}),
                    };
                });

            if (shortList.length) {
                dispatch(setShortList(shortListData));
            }

            if (longList.length) {
                dispatch(setLongListWithIn(longListData));
            }

            return [...shortListData, ...longListData];
        };

        initialFetch();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update users if filter or checkboxes changes
    useEffect(() => {
        setMembers((members) =>
            members.map((member) => {
                if (checkboxesState.longList) {
                    if (member.in === undefined) {
                        return {
                            ...member,
                            visible: member.name.toLowerCase().includes(filterValue.toLowerCase()),
                        };
                    }
                }
                if (checkboxesState.shortList) {
                    if (member.in === "shortList") {
                        return {
                            ...member,
                            visible: member.name.toLowerCase().includes(filterValue.toLowerCase()),
                        };
                    }
                }
                return member;
            })
        );
    }, [filterValue, checkboxesState]);

    const getSelectedVacancyContacts = async (lists: Member[] | undefined, selectedVacancy: CRMVacancies) => {
        if (lists === undefined) {
            setLoadingState({ message: loadingMessages.generic, status: false });
            return;
        }

        setLoadingState({ message: loadingMessages.generic, status: true });

        if (selectedVacancy) {
            try {
                const { data } = await fetchContactsByVacancy(selectedVacancy.crimson_vacancyid);
                const exportedMembers = data.data;

                if (exportedMembers.length) {
                    const exportedContactIds = exportedMembers.map((exportedMember) =>
                        Number(exportedMember.contact_id)
                    );

                    setMembers(
                        lists.map((member) => ({
                            ...member,
                            visible: true,
                            isExported: exportedContactIds.includes(member.person_id),
                        }))
                    );

                    setLoadingState({ message: loadingMessages.generic, status: false });
                    return;
                }
            } catch (error) {
                setLoadingState({ message: loadingMessages.generic, status: false });
                console.error(error);
                return;
            }
        }

        setMembers(
            lists.map((member) => ({
                ...member,
                visible: true,
            }))
        );

        setLoadingState({ message: loadingMessages.generic, status: false });
    };

    const onCloseDialog = () => {
        onClose(false);
        setFilterValue("");
    };

    const handleChecked = (ev: ChangeEvent<HTMLInputElement>, list: "shortList" | "longList") => {
        setCheckboxesState((prevValue) => ({ ...prevValue, [list]: ev.target.checked }));
        setMembers((members) =>
            members.map((member) => {
                if (list === "shortList" && member.in === list) {
                    return { ...member, visible: ev.target.checked };
                }
                if (list === "longList" && member.in === undefined) {
                    return { ...member, visible: ev.target.checked };
                }
                return member;
            })
        );
    };

    const handleToggleMember = (memberId: number) => {
        if (!memberId) return;
        setMembers((members) =>
            members.map((member) => {
                if (member.person_id === memberId) {
                    return {
                        ...member,
                        isSelected: !member.isSelected,
                    };
                }
                return member;
            })
        );
    };

    const handleToggleAll = () => {
        setMembers((members) =>
            members.map((member) => ({
                ...member,
                isSelected: !member.isExported && member.visible ? !areAllVisibleMembersSelected : false,
            }))
        );
    };

    const handleExportLeaders = async () => {
        if (!selectedVacancy) return;

        try {
            setLoadingState({ message: loadingMessages.exporting, status: true });
            const promises: any = [];
            const peopleIds = members.reduce((list, member) => {
                return member.isSelected ? [...list, `${member.person_id}`] : list;
            }, [] as string[]);

            let peopleIdChunks = breakArrayIntoChunks<string>([...peopleIds], 20);

            peopleIdChunks.forEach(async (peopleId) => {
                const promise = addContactsToCrm(peopleId);
                promises.push(promise);
            });

            const addContactResponse = await Promise.allSettled(promises);
            if (addContactResponse.some((response) => response.status === "rejected")) {
                throw new Error("Error adding contacts");
            }

            peopleIdChunks = breakArrayIntoChunks<string>([...peopleIds], 5);

            peopleIdChunks.forEach(async (peopleId) => {
                const promise = saveContactToVacancy((selectedVacancy as CRMVacancies).crimson_vacancyid, peopleId);
                promises.push(promise);
            });

            const saveContactResponse = await Promise.allSettled(promises);
            if (saveContactResponse.some((response) => response.status === "rejected")) {
                throw new Error("Error saving contacts");
            }

            await Promise.allSettled(promises);

            if ([...addContactResponse, ...saveContactResponse].every((response) => response.status === "fulfilled")) {
                setMembers((members) =>
                    members.map((member) => ({ ...member, isExported: member.isSelected, isSelected: false }))
                );
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingState((prevValue) => ({ ...prevValue, status: false }));
        }
    };

    const handleSelectVacancy = (vacancy: TVacancy | string | null) => {
        if (typeof vacancy === "string" || vacancy === null) {
            return;
        }
        const newVacancy = vacancy as unknown as CRMVacancies;
        setSelectedVacancy(newVacancy);
        getSelectedVacancyContacts(members, newVacancy);
    };

    return (
        <Dialog open={open} PaperProps={{ className: classes.customDialogPaper }}>
            <Box display="flex" justifyContent="flex-end" paddingTop={1} paddingRight={1}>
                <IconButton aria-label="close" size="small" onClick={onCloseDialog}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box className={classes.dialogHeader}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {intl.formatMessage({ id: "crm.dialog.title" })}
                </Typography>
                <Box display="flex">
                    <NavButton
                        showBackArrow={false}
                        endIcon={<CustomIcon icon={icons.share} />}
                        translationId="action.export"
                        disabled={!members.some((member) => member.isSelected) || isEmpty(selectedVacancy)}
                        onClick={handleExportLeaders}
                    />
                </Box>
            </Box>
            <Box p="relative">
                {loadingState.status && (
                    <Box className={classes.loadingBlurryBg}>
                        <Box className={classes.loadingWrapper}>
                            <CircularProgress color="primary" />
                            <Typography className={classes.loadingMessage}>{loadingState.message}</Typography>
                        </Box>
                    </Box>
                )}
                <DialogContent>
                    {!isEmpty(releatedBrief?.data.crm_vacancy) ? (
                        <Box className={classes.selectedVacancyContainer}>
                            <CustomIcon icon={icons.users} className={classes.usersIcon} />
                            <Typography variant="h6" className={classes.vacancy}>
                                <strong>Selected CRM Vacancy:</strong> {releatedBrief?.data.crm_vacancy?.crimson_name}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box className={classes.noVacancyContainer}>
                                <Box className={classes.selectVacancy}>
                                    <CustomIcon icon={icons.users} className={classes.usersIcon} />
                                    <Box sx={{ fontSize: 16 }}>
                                        <Typography>
                                            {intl.formatMessage({ id: "crm.dialog.noVacancy.description" })}
                                        </Typography>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {intl.formatMessage({ id: "crm.dialog.noVacancy.select" })}
                                        </Typography>
                                    </Box>
                                </Box>

                                <AutocompleteField
                                    blurOnSelect
                                    inputSize="small"
                                    showStartAdorment
                                    id="brief-vacancy"
                                    showEndAdorment={false}
                                    options={vacancyList}
                                    placeholder={intl.formatMessage({ id: "crm.dialog.autocomplete.placeholder" })}
                                    renderOption={renderOptionVacancy}
                                    getOptionLabel={getOptionLabelVacancy}
                                    value={selectedVacancy}
                                    onOptionSelectCallback={handleSelectVacancy}
                                />
                            </Box>
                        </>
                    )}

                    <Box marginBottom={2} marginTop={2}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {intl.formatMessage({ id: "crm.dialog.subheader1" })}
                        </Typography>
                        <Typography variant="subtitle2" className={classes.subheaderText}>
                            {intl.formatMessage({ id: "crm.dialog.subheader2" })}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" marginBottom={1}>
                        <FormGroup row>
                            <Box className={classes.checkboxContainer}>
                                <FormControlLabel
                                    label="Shortlist"
                                    sx={{ mr: 1 }}
                                    control={
                                        <Checkbox
                                            checked={checkboxesState.shortList}
                                            disabled={
                                                !checkboxesState.longList ||
                                                !members.filter((member) => member.in === "shortList").length ||
                                                !members.filter((member) => member.in === undefined).length
                                            }
                                            onChange={(ev) => handleChecked(ev, "shortList")}
                                            size="small"
                                        />
                                    }
                                />
                                <CustomIcon icon={icons.shortListIcon} className={classes.shortlistIcon} />
                            </Box>
                            <Box className={classes.checkboxContainer}>
                                <FormControlLabel
                                    label="Longlist"
                                    sx={{ mr: 1 }}
                                    control={
                                        <Checkbox
                                            checked={checkboxesState.longList}
                                            disabled={
                                                !checkboxesState.shortList ||
                                                !members.filter((member) => member.in === undefined).length ||
                                                !members.filter((member) => member.in === "shortList").length
                                            }
                                            onChange={(ev) => handleChecked(ev, "longList")}
                                            size="small"
                                        />
                                    }
                                />
                                <CustomIcon icon={icons.longListIcon} className={classes.longlistIcon} />
                            </Box>
                        </FormGroup>
                        <Box>
                            <FilterFieldButton
                                value={filterValue}
                                setValue={setFilterValue}
                                placeholder={intl.formatMessage({ id: "crm.dialog.filterLeaders" })}
                            />
                        </Box>
                    </Box>

                    <Box
                        className={classes.leadersListContainer}
                        sx={{
                            maxHeight: isEmpty(releatedBrief?.data.crm_vacancy) ? 315 : 376,
                        }}
                    >
                        <Box display="flex" justifyContent="flex-end">
                            <IconButton
                                aria-label="select all"
                                onClick={handleToggleAll}
                                sx={{ display: "flex", alignItems: "center" }}
                            >
                                {areAllVisibleMembersSelected ? (
                                    <CancelIcon className={classes.removeIcon} sx={{ fontSize: 16, mr: 0.5 }} />
                                ) : (
                                    <CheckCircleRoundedIcon
                                        className={classes.addIcon}
                                        sx={{ fontSize: 16, mr: 0.5 }}
                                    />
                                )}
                                <Typography
                                    className={classes.selectAllText}
                                    sx={(theme) => ({
                                        color: areAllVisibleMembersSelected
                                            ? theme.palette.error.main
                                            : theme.palette.success.main,
                                    })}
                                >
                                    {intl.formatMessage({
                                        id: areAllVisibleMembersSelected
                                            ? "crm.dialog.deselectAll"
                                            : "crm.dialog.selectAll",
                                    })}
                                </Typography>
                            </IconButton>
                        </Box>
                        {members
                            .filter((member) => member.visible)
                            .map((member) => (
                                <Box
                                    key={member.person_id}
                                    className={cn({
                                        [classes.leaderRowSelected]: member.isSelected,
                                        [classes.leaderRowExported]: member.isExported,
                                    })}
                                >
                                    <Box className={classes.leaderRowWrapper}>
                                        {/* Leader Role */}
                                        <Box className={classes.leaderRoleContainer}>
                                            <Typography
                                                className={cn(classes.leaderRoleContainer, {
                                                    [classes.leaderNoRole]: !member.role,
                                                })}
                                                sx={{ fontWeight: member.isSelected ? 700 : 400 }}
                                            >
                                                {member.role || "no role"}
                                            </Typography>
                                        </Box>

                                        <Divider orientation="vertical" flexItem className={classes.leaderRowDivider} />

                                        {/* Leader shortlist/longlist icon */}
                                        <Box display="flex" alignItems="center" ml={1} mr={1}>
                                            <CustomIcon
                                                icon={
                                                    member.in === "shortList" ? icons.shortListIcon : icons.longListIcon
                                                }
                                                className={
                                                    member.in === "shortList"
                                                        ? classes.shortlistIcon
                                                        : classes.longlistIcon
                                                }
                                            />
                                        </Box>

                                        <Divider orientation="vertical" flexItem className={classes.leaderRowDivider} />

                                        {/* Leader Name */}
                                        <Box className={classes.leaderNameWrapper}>
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: member.isSelected ? 700 : 400 }}
                                            >
                                                {capitalize(member.name)}
                                            </Typography>
                                        </Box>

                                        {/* Add to Export Action */}
                                        {member?.isExported ? (
                                            <Box display="flex" alignItems="center=" p={1}>
                                                <CheckIcon
                                                    sx={(theme) => ({
                                                        fontSize: 18,
                                                        color: theme.palette.text.disabled,
                                                    })}
                                                />
                                            </Box>
                                        ) : (
                                            <IconButton
                                                aria-label="add"
                                                onClick={() => handleToggleMember(member.person_id)}
                                            >
                                                <CheckCircleRoundedIcon
                                                    sx={(theme) => ({
                                                        fontSize: 20,
                                                        color: member.isSelected
                                                            ? theme.palette.success.main
                                                            : `${theme.palette.success.main}50`,
                                                    })}
                                                />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Divider orientation="horizontal" className={classes.leaderRowHorizontalDivider} />
                                </Box>
                            ))}
                        {!members.filter((member) => member.visible).length && filterValue && (
                            <Box className={classes.noResults}>
                                <Typography variant="subtitle2" sx={{ margin: "auto" }}>
                                    {intl.formatMessage({ id: "dashboard.filters.noResults" })}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            </Box>
        </Dialog>
    );
};

export default CRMExport;
